import { useCallback, useRef } from "react"

import { RaceShield } from "@/components/race-shield"
import { Button } from "@/components/ui/button"
import { getTeamHue } from "@/lib/race-colors"
import { cn } from "@/lib/utils"

interface HuePickerProps {
  id?: string
  race: string
  /** Override the shield initials (e.g. team name initials). */
  shieldLabel?: string
  value: number | null
  onChange: (hue: number | null) => void
  /** Fires when the user finishes dragging (pointer up). Useful for debounced saves. */
  onChangeEnd?: (hue: number | null) => void
}

export function HuePicker({
  id,
  race,
  shieldLabel,
  value,
  onChange,
  onChangeEnd,
}: HuePickerProps) {
  const resolvedHue = getTeamHue(race, value)
  const trackRef = useRef<HTMLDivElement>(null)
  const latestHueRef = useRef<number | null>(value)

  const handlePointerEvent = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return
      const rect = trackRef.current.getBoundingClientRect()
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      const hue = Math.round(pct * 360)
      latestHueRef.current = hue
      onChange(hue)
    },
    [onChange],
  )

  return (
    <div className="flex items-center gap-4">
      <RaceShield race={race} hue={resolvedHue} label={shieldLabel} size="md" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div
          ref={trackRef}
          className="relative h-6 cursor-pointer rounded-sm"
          style={{
            background:
              "linear-gradient(to right, oklch(0.55 0.20 0), oklch(0.55 0.20 60), oklch(0.55 0.20 120), oklch(0.55 0.20 180), oklch(0.55 0.20 240), oklch(0.55 0.20 300), oklch(0.55 0.20 360))",
          }}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId)
            handlePointerEvent(e.clientX)
          }}
          onPointerMove={(e) => {
            if (e.buttons > 0) handlePointerEvent(e.clientX)
          }}
          onPointerUp={() => {
            if (onChangeEnd) onChangeEnd(latestHueRef.current)
          }}
          id={id}
          role="slider"
          aria-label="Team color hue"
          aria-valuemin={0}
          aria-valuemax={360}
          aria-valuenow={resolvedHue}
        >
          {/* Thumb */}
          <div
            className="absolute top-0 h-full w-1.5 -translate-x-1/2 rounded-sm border border-white/80"
            style={{
              left: `${(resolvedHue / 360) * 100}%`,
              background: `oklch(0.55 0.20 ${resolvedHue})`,
              boxShadow: "0 0 4px oklch(0 0 0 / 0.5)",
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">
            Hue: {resolvedHue}°
          </span>
          {value != null && (
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                onChange(null)
                if (onChangeEnd) onChangeEnd(null)
              }}
              className={cn(
                "text-muted-foreground hover:text-foreground text-xs transition-colors",
                "h-auto p-0",
              )}
            >
              Reset to race default
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
